<template>
  <div class="analytics-panel">
    <div v-if="campaignStatus === 'draft'" class="analytics-empty">
      <i class="ri-bar-chart-box-line"></i>
      <p>活動尚未啟用，啟用後將開始追蹤參與與成效數據。</p>
    </div>

    <template v-else>
      <div class="analytics-toolbar">
        <div class="date-filters">
          <div class="filter-item">
            <label>起始日期</label>
            <input v-model="startDate" type="date" class="form-control" />
          </div>
          <div class="filter-item">
            <label>結束日期</label>
            <input v-model="endDate" type="date" class="form-control" />
          </div>
          <button type="button" class="btn btn-secondary btn-sm" @click="resetDateRange">清除區間</button>
        </div>
        <button type="button" class="btn btn-outline-primary btn-sm" :disabled="loading" @click="load">
          <i class="ri-refresh-line"></i>
          重新整理
        </button>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入成效數據...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p class="error-message">{{ error }}</p>
        <button type="button" class="btn btn-secondary btn-sm" @click="load">重試</button>
      </div>

      <template v-else-if="summary">
        <div v-if="summary.budgetWarnings?.length" class="budget-warnings">
          <p v-for="(w, idx) in summary.budgetWarnings" :key="idx">
            <i class="ri-alert-line"></i>
            {{ w.message }}
          </p>
        </div>

        <section class="metric-section">
          <h4>參與概況</h4>
          <div class="metric-grid">
            <div class="metric-card">
              <span class="metric-label">參與人數</span>
              <strong class="metric-value">{{ summary.funnel.participants }}</strong>
            </div>
            <div class="metric-card">
              <span class="metric-label">完成人數</span>
              <strong class="metric-value">{{ summary.funnel.completed }}</strong>
            </div>
            <div class="metric-card">
              <span class="metric-label">進行中</span>
              <strong class="metric-value">{{ summary.funnel.active }}</strong>
            </div>
            <div class="metric-card">
              <span class="metric-label">完成率</span>
              <strong class="metric-value">{{ formatPercent(summary.funnel.completionRate) }}</strong>
            </div>
          </div>
        </section>

        <section class="metric-section">
          <h4>發獎與推播</h4>
          <div class="metric-grid">
            <div class="metric-card">
              <span class="metric-label">LINE 推播成功</span>
              <strong class="metric-value">{{ summary.push.success }}</strong>
              <span v-if="summary.push.failed" class="metric-sub">失敗 {{ summary.push.failed }}</span>
            </div>
            <div class="metric-card">
              <span class="metric-label">優惠券發放</span>
              <strong class="metric-value">{{ summary.coupon.issued }}</strong>
              <span class="metric-sub">核銷 {{ summary.coupon.used }}</span>
            </div>
            <div class="metric-card">
              <span class="metric-label">點數發放</span>
              <strong class="metric-value">{{ formatNumber(summary.points.issued) }}</strong>
            </div>
            <div class="metric-card">
              <span class="metric-label">抽獎觸發</span>
              <strong class="metric-value">{{ summary.lottery.triggered }}</strong>
            </div>
            <div class="metric-card">
              <span class="metric-label">集章次數</span>
              <strong class="metric-value">{{ summary.stamp.collected }}</strong>
            </div>
          </div>
        </section>

        <section class="metric-section">
          <h4>轉換與 ROI</h4>
          <div class="metric-grid">
            <div class="metric-card">
              <span class="metric-label">轉換訂單</span>
              <strong class="metric-value">{{ summary.conversion.orderCount }}</strong>
            </div>
            <div class="metric-card">
              <span class="metric-label">轉換營收</span>
              <strong class="metric-value">{{ formatMoney(summary.conversion.revenue) }}</strong>
            </div>
            <div class="metric-card">
              <span class="metric-label">平均客單</span>
              <strong class="metric-value">{{ formatMoney(summary.conversion.avgOrderValue) }}</strong>
            </div>
            <div class="metric-card highlight">
              <span class="metric-label">ROI</span>
              <strong class="metric-value">{{ formatRoi(summary.roi.value) }}</strong>
            </div>
          </div>
        </section>

        <section class="metric-section">
          <h4>成本明細</h4>
          <div class="cost-table">
            <div class="cost-row">
              <span>LINE 推播（{{ summary.cost.lineMessageCount }} 則 × {{ summary.cost.lineMessageUnitCost }} 元）</span>
              <strong>{{ formatPushCost(summary.cost.lineMessageCost) }}</strong>
            </div>
            <div class="cost-row">
              <span>點數成本（{{ formatNumber(summary.cost.pointsIssued) }} 點 × {{ summary.cost.pointsCostRate }} 元）</span>
              <strong>{{ formatMoney(summary.cost.pointsCost) }}</strong>
            </div>
            <div class="cost-row">
              <span>優惠券核銷折抵</span>
              <strong>{{ formatMoney(summary.cost.couponRedeemedDiscount) }}</strong>
            </div>
            <div class="cost-row total">
              <span>總成本</span>
              <strong>{{ formatMoney(summary.cost.totalCost) }}</strong>
            </div>
            <div v-if="summary.budgetCap != null" class="cost-row">
              <span>預算上限</span>
              <strong>{{ formatMoney(summary.budgetCap) }}</strong>
            </div>
          </div>
        </section>

        <section v-if="funnel?.steps?.length" class="metric-section">
          <h4>
            步驟漏斗
            <span v-if="funnel.avgCompletionHours != null" class="section-meta">
              平均完成 {{ funnel.avgCompletionHours }} 小時
            </span>
          </h4>
          <div class="funnel-table-wrapper">
            <table class="funnel-table">
              <thead>
                <tr>
                  <th>步驟</th>
                  <th>達成人數</th>
                  <th>流失率</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="step in funnel.steps" :key="step.stepId">
                  <td>
                    <span class="step-badge">{{ step.stepOrder }}</span>
                    {{ step.stepName }}
                  </td>
                  <td>{{ step.reachedCount }}</td>
                  <td>
                    <span v-if="step.dropOffRate != null">{{ step.dropOffRate }}%</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <p v-if="isAllZero" class="analytics-hint">
          目前尚無參與或執行紀錄。會員觸發活動事件後，數據將自動更新。
        </p>
      </template>
    </template>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { marketingCampaignService } from '@/api/modules/marketingCampaigns.js';
import { formatPushCost } from '@/constants/marketingCampaign.js';

export default {
  name: 'MarketingCampaignAnalyticsPanel',
  props: {
    campaignId: { type: String, required: true },
    campaignStatus: { type: String, default: 'draft' }
  },
  setup(props) {
    const loading = ref(false);
    const error = ref(null);
    const summary = ref(null);
    const funnel = ref(null);
    const startDate = ref('');
    const endDate = ref('');

    const buildParams = () => {
      const params = {};
      if (startDate.value) params.startDate = `${startDate.value}T00:00:00.000Z`;
      if (endDate.value) params.endDate = `${endDate.value}T23:59:59.999Z`;
      return params;
    };

    const load = async () => {
      if (!props.campaignId || props.campaignStatus === 'draft') return;
      loading.value = true;
      error.value = null;
      try {
        const params = buildParams();
        const [summaryRes, funnelRes] = await Promise.all([
          marketingCampaignService.getAnalyticsSummary(props.campaignId, params),
          marketingCampaignService.getAnalyticsFunnel(props.campaignId, params)
        ]);
        summary.value = summaryRes.data?.data ?? summaryRes.data;
        funnel.value = funnelRes.data?.data ?? funnelRes.data;
      } catch (e) {
        error.value = e.response?.data?.message || e.message || '載入成效失敗';
        summary.value = null;
        funnel.value = null;
      } finally {
        loading.value = false;
      }
    };

    const resetDateRange = () => {
      startDate.value = '';
      endDate.value = '';
    };

    const formatMoney = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return '-';
      return `$${num.toLocaleString('zh-TW', { maximumFractionDigits: 0 })}`;
    };

    const formatNumber = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return '0';
      return num.toLocaleString('zh-TW');
    };

    const formatPercent = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return '-';
      return `${num.toFixed(1)}%`;
    };

    const formatRoi = (value) => {
      if (value == null) return '無成本紀錄';
      const num = Number(value);
      if (!Number.isFinite(num)) return '-';
      return `${(num * 100).toFixed(1)}%`;
    };

    const isAllZero = computed(() => {
      if (!summary.value) return false;
      return (
        (summary.value.funnel?.participants || 0) === 0
        && (summary.value.push?.success || 0) === 0
        && (summary.value.coupon?.issued || 0) === 0
      );
    });

    watch([startDate, endDate], () => {
      if (props.campaignStatus !== 'draft') load();
    });

    watch(
      () => [props.campaignId, props.campaignStatus],
      () => load(),
      { immediate: false }
    );

    onMounted(() => load());

    return {
      loading,
      error,
      summary,
      funnel,
      startDate,
      endDate,
      load,
      resetDateRange,
      formatMoney,
      formatNumber,
      formatPercent,
      formatRoi,
      formatPushCost,
      isAllZero
    };
  }
};
</script>

<style scoped>
.analytics-panel {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
}

.analytics-empty {
  text-align: center;
  padding: 48px 16px;
  color: #6c757d;
}

.analytics-empty i {
  font-size: 40px;
  margin-bottom: 12px;
  display: block;
}

.analytics-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.date-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
}

.filter-item label {
  display: block;
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 4px;
}

.filter-item .form-control {
  min-width: 150px;
}

.budget-warnings {
  margin-bottom: 16px;
  padding: 10px 12px;
  background: #fff8e6;
  border: 1px solid #ffe08a;
  border-radius: 6px;
  color: #b45309;
  font-size: 13px;
}

.budget-warnings p {
  margin: 0;
}

.budget-warnings p + p {
  margin-top: 4px;
}

.metric-section {
  margin-bottom: 20px;
}

.metric-section h4 {
  margin: 0 0 10px;
  font-size: 15px;
  color: #343a40;
}

.section-meta {
  font-size: 12px;
  font-weight: normal;
  color: #6c757d;
  margin-left: 8px;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
}

.metric-card {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #eee;
}

.metric-card.highlight {
  background: #eef4ff;
  border-color: #cfe2ff;
}

.metric-label {
  display: block;
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 4px;
}

.metric-value {
  font-size: 20px;
  color: #212529;
}

.metric-sub {
  display: block;
  font-size: 11px;
  color: #868e96;
  margin-top: 2px;
}

.cost-table {
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
}

.cost-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 14px;
  font-size: 14px;
  border-bottom: 1px solid #f0f0f0;
}

.cost-row:last-child {
  border-bottom: none;
}

.cost-row.total {
  background: #f8f9fa;
  font-weight: 600;
}

.funnel-table-wrapper {
  overflow-x: auto;
}

.funnel-table {
  width: 100%;
  min-width: 400px;
  border-collapse: collapse;
}

.funnel-table th,
.funnel-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}

.funnel-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #495057;
}

.step-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #0d6efd;
  color: #fff;
  font-size: 11px;
  margin-right: 8px;
}

.analytics-hint {
  margin: 0;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 13px;
  color: #6c757d;
}

.text-muted {
  color: #adb5bd;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 32px 16px;
}
</style>
